import type { Report, Resource, ResourceHome, SubjectInfo } from '@tnn/core'
import { accessInvite, accessLocales, accessRequest } from './access'
import {
  account,
  accountingLocales,
  bill,
  billingNote,
  billPayment,
  creditNote,
  customer,
  customerReceipt,
  invoice,
  payment,
} from './accounting'
import { adminLocales, auditLog, role, user } from './admin'
import { boardsLocales } from './boards'
import {
  cashBond,
  cashClaim,
  cashDeposit,
  cashEntry,
  cashExpense,
  cashLocales,
  cashMove,
  cashReserve,
  cashSafe,
  cashTransfer,
} from './cash'
import { asset, assetHandover, branch, company, companyLocales, uniform } from './company'
import { driveLocales } from './drive'
import { driverLocales } from './driver'
import {
  application,
  clockDisplay,
  department,
  employee,
  holiday,
  hrLocales,
  leave,
  leaveAllowance,
  leaveType,
  overtime,
  position,
  profile,
  schedule,
  timeEntry,
} from './hr'
import {
  advance,
  compensation,
  fuelRate,
  payAdjustment,
  payItem,
  payrollLocales,
  payrollRun,
  payrollSetting,
  payslip,
  taxDeclaration,
  tripRate,
} from './payroll'
import {
  contact,
  deliveryRun,
  driver,
  manifest,
  product,
  receipt,
  refund,
  shipment,
  tmsLocales,
  trip,
  vehicle,
  waybill,
  waybillReport,
} from './tms'

/**
 * Every resource in the platform. Adding a resource here is what makes it
 * appear in the permission matrix, the generic API and the import/export
 * tools; nothing else needs registering.
 */
export const resources: readonly Resource[] = [
  waybill,
  receipt,
  refund,
  shipment,
  trip,
  deliveryRun,
  manifest,
  contact,
  product,
  branch,
  vehicle,
  driver,
  customer,
  invoice,
  payment,
  billingNote,
  creditNote,
  customerReceipt,
  account,
  bill,
  billPayment,
  cashTransfer,
  cashDeposit,
  cashReserve,
  cashExpense,
  cashClaim,
  cashEntry,
  cashBond,
  cashSafe,
  cashMove,
  employee,
  department,
  position,
  profile,
  application,
  leaveType,
  leaveAllowance,
  leave,
  schedule,
  holiday,
  timeEntry,
  clockDisplay,
  overtime,
  compensation,
  payItem,
  payAdjustment,
  tripRate,
  fuelRate,
  payrollRun,
  payslip,
  taxDeclaration,
  payrollSetting,
  advance,
  accessInvite,
  accessRequest,
  user,
  role,
  auditLog,
  company,
  asset,
  assetHandover,
  uniform,
]

const byKey = new Map(resources.map((r) => [r.key, r]))

export function getResource(key: string): Resource {
  const resource = byKey.get(key)
  if (!resource) throw new Error(`Unknown resource "${key}"`)
  return resource
}

export function findResource(key: string): Resource | undefined {
  return byKey.get(key)
}

export function resourcesOf(app: ResourceHome): Resource[] {
  return resources.filter((r) => r.app === app)
}

/** Every report (`defineReport`): each app's reports page shows its own, and each has a guide of its key */
export const reports: readonly Report[] = [waybillReport]

/** Input for the permission engine in `@tnn/core`. */
export const subjects: readonly SubjectInfo[] = resources.map((r) => r.subjectInfo)

/** Translations contributed by each module, registered as i18n namespaces. */
export const domainLocales = {
  th: {
    tms: tmsLocales.th,
    accounting: accountingLocales.th,
    hr: hrLocales.th,
    payroll: payrollLocales.th,
    admin: adminLocales.th,
    cash: cashLocales.th,
    access: accessLocales.th,
    company: companyLocales.th,
    driver: driverLocales.th,
    drive: driveLocales.th,
    boards: boardsLocales.th,
  },
  en: {
    tms: tmsLocales.en,
    accounting: accountingLocales.en,
    hr: hrLocales.en,
    payroll: payrollLocales.en,
    admin: adminLocales.en,
    cash: cashLocales.en,
    access: accessLocales.en,
    company: companyLocales.en,
    driver: driverLocales.en,
    drive: driveLocales.en,
    boards: boardsLocales.en,
  },
} as const
